<template>
    <section>
        <header class="modal-card-head is-radiusless">
            <h5 class="title is-5">Update Task Dates - "{{ task.taskText }}"</h5>
        </header>
        <section class="modal-card-body">
            <div class="grid gap-4 lg:grid-cols-2">
                <div>
                    <condo-form-item label="Start Date (In Progress)">
                        <condo-date-picker type="date" v-model="formData.startDate" :clearable="true" class="w-full" />
                    </condo-form-item>
                </div>

                <div>
                    <condo-form-item label="Expected Date">
                        <condo-date-picker type="date" v-model="formData.expectedDate" :clearable="true" class="w-full" />
                    </condo-form-item>
                </div>

                <div>
                    <condo-form-item label="Due Date">
                        <template #label-extension>
                            <div class="ml-6 flex rounded bg-gray-100 p-0.5 text-xs">
                                <button
                                    @click="dueDateType = 'date'"
                                    :class="[
                                        'rounded px-1.5 py-0.5 text-xs font-medium transition-all duration-200',
                                        dueDateType === 'date' ? 'bg-emerald-500 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                                    ]"
                                    type="button"
                                >
                                    Date
                                </button>
                                <button
                                    @click="dueDateType = 'week'"
                                    :class="[
                                        'rounded px-1.5 py-0.5 text-xs font-medium transition-all duration-200',
                                        dueDateType === 'week' ? 'bg-emerald-500 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                                    ]"
                                    type="button"
                                >
                                    Friday of Week
                                </button>
                            </div>
                        </template>
                        <condo-date-picker
                            :key="dueDateType"
                            v-model="formData.dueDate"
                            :type="dueDateType"
                            :clearable="true"
                            class="w-full"
                            @update:value="onDueDateChanged"
                        />
                    </condo-form-item>
                </div>

                <div>
                    <condo-form-item label="Completion Date" :message="!isCompleted ? 'Can be edited when task status is \'Completed\'' : undefined">
                        <condo-date-picker type="date" v-model="formData.completionDate" :clearable="true" :disabled="!isCompleted" class="w-full" />
                    </condo-form-item>
                </div>

                <div>
                    <condo-form-item label="Exp / Act. Date" message="Completion Date if completed, otherwise Expected Date, otherwise Due Date">
                        <condo-date-picker type="date" :model-value="computedExpActDate" :disabled="true" class="w-full" />
                    </condo-form-item>
                </div>
            </div>
        </section>
        <footer class="modal-card-foot">
            <div class="is-flex is-justify-content-end is-fullwidth">
                <n-button-group>
                    <condo-button icon-left="window-close" @click="emit('close')" type="is-default" ghost size="small">Close</condo-button>
                    <condo-button icon-left="save" @click="save()" type="is-primary" size="small">Update</condo-button>
                </n-button-group>
            </div>
        </footer>
    </section>
</template>

<script setup lang="ts">
import { ITask, TaskStatus, taskToMilestoneExpectedDate } from '@condo/domain';
import { computed, ref, watch } from 'vue';
import { NButtonGroup } from 'naive-ui';
import CondoFormItem from '@/components/tasks/v2/CondoFormItem.vue';
import dayjs from 'dayjs';

interface Props {
    task: Partial<ITask>;
}

interface Emits {
    (ev: 'close'): void;
    (ev: 'save', data: { startDate?: Date; expectedDate?: Date; dueDate?: Date; completionDate?: Date }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isCompleted = computed(() => props.task.status === TaskStatus.Completed);

const parseDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const computedExpActDate = computed(() => {
    const taskWithFormData: Partial<ITask> = {
        ...props.task,
        expectedDate: formData.value.expectedDate,
        dueDate: formData.value.dueDate,
        statusUpdatedAt: {
            ...props.task.statusUpdatedAt,
            [TaskStatus.Completed]: formData.value.completionDate,
        },
    };
    const result = taskToMilestoneExpectedDate(taskWithFormData as ITask);
    return result ? parseDate(result) : undefined;
});

// Extract dates from the statusUpdatedAt record
const getStatusDate = (status: TaskStatus): Date | undefined => {
    if (!props.task.statusUpdatedAt) return undefined;
    const dateValue = props.task.statusUpdatedAt[status];
    return parseDate(dateValue);
};

const dueDateType = ref<'date' | 'week'>('week');

// Determine initial dueDateType based on if dueDate is a Friday
if (props.task.dueDate) {
    const dueDate = dayjs(props.task.dueDate);
    // SKB-520: Use friday of the week selected.
    if (dueDate.day() === 5) {
        dueDateType.value = 'week';
    } else {
        dueDateType.value = 'date';
    }
}

const formData = ref({
    startDate: getStatusDate(TaskStatus.InProgress),
    expectedDate: parseDate(props.task.expectedDate),
    dueDate: parseDate(props.task.dueDate),
    completionDate: getStatusDate(TaskStatus.Completed),
});

const getFridayOfWeek = (weekDate: number): Date => {
    const date = dayjs(weekDate);
    const dayOfWeek = date.day();
    const daysToFriday = 5 - dayOfWeek;
    return date.add(daysToFriday, 'day').toDate();
};

const onDueDateChanged = (weekDate: number) => {
    if (dueDateType.value !== 'week') {
        return;
    }
    if (weekDate) {
        formData.value.dueDate = getFridayOfWeek(weekDate);
    } else {
        formData.value.dueDate = undefined;
    }
};

watch(dueDateType, (newValue, oldValue) => {
    if (newValue === 'date' && oldValue === 'week' && formData.value.dueDate) {
        formData.value.dueDate = getFridayOfWeek(new Date(formData.value.dueDate).getTime());
    }
});

const save = () => {
    emit('save', formData.value);
};
</script>
